/**
 * @file ThemeProvider.tsx
 * @brief Light / dark theme provider with localStorage persistence.
 *
 * @details
 * - The `useTheme()` hook and the context object live in `themeContext.ts`
 *   so this module exports only a component and keeps Fast Refresh.
 * - Toggles `document.documentElement` className (`light` / `dark`) so the
 *   CSS variable set in `index.css` switches accordingly.
 * - Reads initial theme from `localStorage` (`verblizr.webpanel.theme`)
 *   falling back to OS preference (`prefers-color-scheme`) and finally
 *   light.
 *
 * @dependencies
 * - React Context
 *
 * @references
 * - WEBPANEL/src/index.css (CSS variable definitions for both themes)
 * - WEBPANEL/tailwind.config.ts (`darkMode: ["class"]`)
 *
 * @author DES
 * @date 26-05-2026
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ThemeContext,
  type Theme,
  type ThemeContextValue,
} from "./theme-context";

// ----------------------------------------------------------------------
// Types & constants
// ----------------------------------------------------------------------
const STORAGE_KEY = "verblizr.webpanel.theme";

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------
function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "system";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch {
    // ignore storage errors (private mode, quota, etc.)
  }
  return "system";
}

function applyThemeClass(resolved: "light" | "dark") {
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(resolved);
}

// ----------------------------------------------------------------------
// Provider
// ----------------------------------------------------------------------
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">(() =>
    readStoredTheme() === "system" ? getSystemTheme() : (readStoredTheme() as "light" | "dark"),
  );

  // Apply class to <html> whenever resolved theme changes
  useEffect(() => {
    applyThemeClass(resolvedTheme);
  }, [resolvedTheme]);

  // Recompute resolved theme when user choice or OS preference changes
  useEffect(() => {
    if (theme !== "system") {
      setResolvedTheme(theme);
      return;
    }

    setResolvedTheme(getSystemTheme());
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) =>
      setResolvedTheme(e.matches ? "dark" : "light");
    media.addEventListener("change", handler);
    return () => media.removeEventListener("change", handler);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
